import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Users, Calendar, BookOpen, MessageSquare,
  TrendingUp, Clock, CheckCircle, AlertCircle, Loader2,
  GraduationCap, Phone, CreditCard, Rocket, UserCheck, Ban,
  DollarSign, Flame, Thermometer, Timer, Video, User,
  ChevronRight, Plus, ExternalLink, RefreshCw
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { toast } from 'sonner';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatMoney } from '../../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FUNNEL_STAGES = [
  { key: 'registered',           label: 'Registered',      icon: Users,        color: 'text-[#1A2E6E]',  bg: 'bg-[#1A2E6E]/8' },
  { key: 'level_tested',         label: 'Level Tested',    icon: GraduationCap,color: 'text-[#6366F1]',  bg: 'bg-[#6366F1]/8' },
  { key: 'consultant_requested', label: 'Consultant',      icon: Phone,        color: 'text-[#F5A623]',  bg: 'bg-[#F5A623]/8' },
  { key: 'awaiting_payment',     label: 'Awaiting Pay',    icon: CreditCard,   color: 'text-[#0EA5E9]',  bg: 'bg-[#0EA5E9]/8' },
  { key: 'approved',             label: 'Approved',        icon: UserCheck,    color: 'text-[#4CAF50]',  bg: 'bg-[#4CAF50]/8' },
  { key: 'active',               label: 'Active',          icon: Rocket,       color: 'text-[#4CAF50]',  bg: 'bg-[#4CAF50]/8' },
  { key: 'payment_expired',      label: 'Plan Expired',    icon: AlertCircle,  color: 'text-[#D72B2B]',  bg: 'bg-[#D72B2B]/8' },
  { key: 'suspended',            label: 'Suspended',       icon: Ban,          color: 'text-[#D72B2B]',  bg: 'bg-[#D72B2B]/8' },
];

const timeAgo = (isoStr) => {
  if (!isoStr) return '';
  const h = Math.floor((Date.now() - new Date(isoStr)) / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const formatClassTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const formatTodayRelative = (iso) => {
  const diffMin = (new Date(iso) - Date.now()) / 60000;
  if (diffMin < 0) return 'started';
  if (diffMin < 60) return `in ${Math.round(diffMin)}min`;
  return `in ${Math.round(diffMin / 60)}h`;
};

const SignalCard = ({ icon: Icon, color, borderColor, title, badge, description, children, link }) => (
  <div className={`bg-white rounded-xl border ${borderColor} overflow-hidden`}>
    <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center gap-2">
      <Icon size={16} className={color} />
      <h3 className={`text-sm font-semibold text-[#1A2E6E]`}>{title}</h3>
      {badge !== undefined && (
        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-semibold ${color} bg-current/10`}
          style={{ backgroundColor: undefined }}
        >
          <span className={`${color}`}>{badge}</span>
        </span>
      )}
      {link && (
        <Link to={link} className="ml-auto text-xs text-[#D72B2B] font-medium flex items-center gap-1">
          Manage <ChevronRight size={12} />
        </Link>
      )}
    </div>
    {description && <p className="px-5 pt-3 text-xs text-[#1A2E6E]/50">{description}</p>}
    <div className="p-3">{children}</div>
  </div>
);

export const AdminDashboard = () => {
  const { settings } = usePublicSettings();
  const { isStrictAdmin } = useAuth();
  const currency = settings.default_currency || 'EUR';
  const formatCurrencyBRL = (value, options = {}) => formatMoney(value, currency, options);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => { fetchDashboardData(); }, [isStrictAdmin]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Revenue is company-wide financial data — strict-admin only (teachers get 403 from the API).
      const [dashRes, pipelineRes, revenueRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard`),
        axios.get(`${API_URL}/api/admin/pipeline`),
        isStrictAdmin ? axios.get(`${API_URL}/api/admin/reports/revenue`) : Promise.resolve({ data: [] }),
      ]);
      setStats(dashRes.data);
      setPipeline(pipelineRes.data);
      setRevenue([...revenueRes.data].sort((a, b) => a.month.localeCompare(b.month)));
    } catch (err) {
      setError('Failed to load dashboard data');
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  const currentMonthRevenue = revenue.length > 0 ? revenue[revenue.length - 1]?.total_brl ?? 0 : 0;
  const prevMonthRevenue = revenue.length > 1 ? revenue[revenue.length - 2]?.total_brl ?? 0 : 0;
  const revenueGrowth = prevMonthRevenue > 0
    ? (((currentMonthRevenue - prevMonthRevenue) / prevMonthRevenue) * 100).toFixed(0)
    : null;

  const todaysClasses = stats?.todays_classes ?? [];
  const needsAttention = (stats?.hot_leads?.length || 0)
    + (stats?.expiring_enrollments?.length || 0)
    + (stats?.students?.pending || 0)
    + (stats?.unread_contacts || 0);

  const greetingHour = new Date().getHours();
  const greeting = greetingHour < 12 ? 'Good morning' : greetingHour < 18 ? 'Good afternoon' : 'Good evening';

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
      <p className="text-red-700 mb-4">{error}</p>
      <button onClick={fetchDashboardData}
        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 mx-auto">
        <RefreshCw size={16} /> Try Again
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">{greeting}</h1>
          <p className="text-[#1A2E6E]/50 mt-1 text-sm">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/calendar"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors text-sm font-medium">
            <Plus size={16} /> New Class
          </Link>
          <Link to="/admin/students"
            className="flex items-center gap-2 px-4 py-2.5 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 transition-colors text-sm font-medium">
            Students
          </Link>
        </div>
      </div>

      {/* Attention banner — only when there are items needing action */}
      {needsAttention > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap items-center gap-3 px-5 py-3 bg-[#F5A623]/5 border border-[#F5A623]/30 rounded-xl"
        >
          <AlertCircle size={16} className="text-[#F5A623] shrink-0" />
          <span className="text-sm font-medium text-[#1A2E6E]">Attention needed —</span>
          <div className="flex flex-wrap gap-2">
            {stats?.students?.pending > 0 && (
              <Link to="/admin/students" className="text-xs px-2.5 py-1 bg-[#F5A623]/10 text-[#C97D0A] rounded-full font-semibold hover:bg-[#F5A623]/20 transition-colors">
                {stats.students.pending} pending approval
              </Link>
            )}
            {stats?.hot_leads?.length > 0 && (
              <Link to="/admin/leads" className="text-xs px-2.5 py-1 bg-[#F5A623]/10 text-[#C97D0A] rounded-full font-semibold hover:bg-[#F5A623]/20 transition-colors">
                {stats.hot_leads.length} hot leads
              </Link>
            )}
            {stats?.expiring_enrollments?.length > 0 && (
              <Link to="/admin/billing" className="text-xs px-2.5 py-1 bg-[#D72B2B]/8 text-[#D72B2B] rounded-full font-semibold hover:bg-[#D72B2B]/15 transition-colors">
                {stats.expiring_enrollments.length} plans expiring
              </Link>
            )}
            {stats?.unread_contacts > 0 && (
              <Link to="/admin/contacts" className="text-xs px-2.5 py-1 bg-[#1A2E6E]/8 text-[#1A2E6E] rounded-full font-semibold hover:bg-[#1A2E6E]/15 transition-colors">
                {stats.unread_contacts} unread contacts
              </Link>
            )}
          </div>
        </motion.div>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Students',   value: stats?.students?.total || 0,    icon: Users,      color: 'text-[#1A2E6E]', bg: 'bg-[#1A2E6E]/8', link: '/admin/students' },
          { label: 'Pending Approval', value: stats?.students?.pending || 0,  icon: Clock,      color: 'text-[#F5A623]', bg: 'bg-[#F5A623]/8',  link: '/admin/students' },
          { label: 'Active Students',  value: stats?.students?.approved || 0, icon: CheckCircle,color: 'text-[#4CAF50]', bg: 'bg-[#4CAF50]/8' },
          { label: 'Classes Today',    value: todaysClasses.length,           icon: Calendar,   color: 'text-[#D72B2B]', bg: 'bg-[#D72B2B]/8',  link: '/admin/calendar' },
        ].map(({ label, value, icon: Icon, color, bg, link }, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-white rounded-xl border border-[#E8EAF0] p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2.5 rounded-xl ${bg}`}>
                <Icon size={20} className={color} />
              </div>
            </div>
            <p className="text-2xl font-bold text-[#1A2E6E] mb-0.5">{value}</p>
            <p className="text-sm text-[#1A2E6E]/60">{label}</p>
            {link && (
              <Link to={link} className="mt-2 inline-flex items-center gap-1 text-xs text-[#D72B2B] font-medium">
                View <ChevronRight size={12} />
              </Link>
            )}
          </motion.div>
        ))}
      </div>

      {/* Revenue stat inline — company-wide financial data, strict-admin only */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {isStrictAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-[#E8EAF0] p-5 sm:col-span-1"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 rounded-xl bg-[#4CAF50]/8">
                <DollarSign size={20} className="text-[#4CAF50]" />
              </div>
            </div>
            <p className="text-2xl font-bold text-[#1A2E6E] mb-0.5">
              {formatCurrencyBRL(currentMonthRevenue, { maximumFractionDigits: 0 })}
            </p>
            <p className="text-sm text-[#1A2E6E]/60">Revenue this month</p>
            {revenueGrowth !== null && (
              <p className={`text-xs font-semibold mt-2 ${Number(revenueGrowth) >= 0 ? 'text-[#4CAF50]' : 'text-[#D72B2B]'}`}>
                {Number(revenueGrowth) >= 0 ? '+' : '-'}{Math.abs(revenueGrowth)}% vs last month
              </p>
            )}
          </motion.div>
        )}

        <div className={`${isStrictAdmin ? 'sm:col-span-2' : 'sm:col-span-3'} bg-white rounded-xl border border-[#E8EAF0] p-5 flex items-center gap-4`}>
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-[#1A2E6E]/50 mb-1">Upcoming classes</p>
              <p className="text-xl font-bold text-[#1A2E6E]">{stats?.classes?.upcoming || 0}</p>
            </div>
            <div>
              <p className="text-xs text-[#1A2E6E]/50 mb-1">Total classes</p>
              <p className="text-xl font-bold text-[#1A2E6E]">{stats?.classes?.total || 0}</p>
            </div>
          </div>
          <Link to="/admin/calendar"
            className="shrink-0 flex items-center gap-2 px-4 py-2.5 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 transition-colors text-sm font-medium">
            Manage
          </Link>
        </div>
      </div>

      {/* TODAY'S SCHEDULE */}
      <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#1A2E6E]" />
            <h2 className="font-semibold text-[#1A2E6E]">Today's Schedule</h2>
            {todaysClasses.length > 0 && (
              <span className="text-xs bg-[#1A2E6E]/8 text-[#1A2E6E] px-2 py-0.5 rounded-full font-semibold">
                {todaysClasses.length}
              </span>
            )}
          </div>
          <Link to="/admin/calendar" className="text-sm text-[#D72B2B] font-medium flex items-center gap-1">
            All classes <ChevronRight size={14} />
          </Link>
        </div>

        {todaysClasses.length > 0 ? (
          <div className="divide-y divide-[#E8EAF0]">
            {todaysClasses.map((cls, i) => {
              const isPast = new Date(cls.scheduled_at) < new Date();
              const diffMin = (new Date(cls.scheduled_at) - Date.now()) / 60000;
              const isImminent = diffMin >= 0 && diffMin <= 30;
              return (
                <motion.div
                  key={cls.id || i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.03 }}
                  className={`flex items-center gap-4 px-5 py-4 ${isImminent ? 'bg-[#4CAF50]/3' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    isImminent ? 'bg-[#4CAF50]/15' : isPast ? 'bg-[#1A2E6E]/5' : 'bg-[#1A2E6E]/8'
                  }`}>
                    <Video className={`w-5 h-5 ${isImminent ? 'text-[#4CAF50]' : isPast ? 'text-[#1A2E6E]/30' : 'text-[#1A2E6E]'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm truncate ${isPast ? 'text-[#1A2E6E]/50' : 'text-[#1A2E6E]'}`}>
                      {cls.title}
                    </p>
                    <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                      {formatClassTime(cls.scheduled_at)} · {cls.duration_minutes}min
                      {cls.teacher_name && ` · ${cls.teacher_name}`}
                      {cls.student_count > 0 && ` · ${cls.student_count} student${cls.student_count !== 1 ? 's' : ''}`}
                    </p>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {!isPast && (
                      <span className={`text-xs font-semibold ${isImminent ? 'text-[#4CAF50]' : 'text-[#1A2E6E]/50'}`}>
                        {formatTodayRelative(cls.scheduled_at)}
                      </span>
                    )}
                    {isPast && <span className="text-xs text-[#1A2E6E]/30">Done</span>}
                    {cls.meeting_link && !isPast && (
                      <a href={cls.meeting_link} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#4CAF50] text-white text-xs font-semibold rounded-lg hover:bg-[#388E3C] transition-colors">
                        Join <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="px-5 py-10 text-center">
            <Calendar className="w-10 h-10 text-[#1A2E6E]/15 mx-auto mb-3" />
            <p className="text-sm text-[#1A2E6E]/50 mb-3">No classes scheduled for today</p>
            <Link to="/admin/calendar"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#D72B2B] text-white text-sm font-semibold rounded-xl hover:bg-[#B71C1C] transition-colors">
              <Plus size={14} /> Schedule a class
            </Link>
          </div>
        )}
      </div>

      {/* Operational signals row */}
      {(stats?.hot_leads?.length > 0 || stats?.cold_leads?.length > 0 || stats?.expiring_enrollments?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {stats.hot_leads?.length > 0 && (
            <div className="bg-white rounded-xl border border-[#F5A623]/40 overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center gap-2">
                <Flame size={15} className="text-[#F5A623]" />
                <h3 className="text-sm font-semibold text-[#1A2E6E]">Hot Leads</h3>
                <span className="ml-auto text-xs bg-[#F5A623]/10 text-[#F5A623] px-2 py-0.5 rounded-full font-semibold">
                  {stats.hot_leads.length}
                </span>
                <Link to="/admin/leads" className="text-xs text-[#D72B2B] font-medium ml-2 flex items-center gap-0.5">
                  Act <ChevronRight size={11} />
                </Link>
              </div>
              <p className="px-5 pt-3 pb-1 text-xs text-[#1A2E6E]/50">Consultant requested — needs follow-up</p>
              <div className="p-2 space-y-1">
                {stats.hot_leads.slice(0, 5).map(lead => (
                  <Link key={lead.id} to="/admin/leads"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F5A623]/5 transition-colors group">
                    <div className="w-8 h-8 rounded-full bg-[#F5A623]/10 flex items-center justify-center text-[#F5A623] font-bold text-sm shrink-0">
                      {lead.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A2E6E] truncate">{lead.name}</p>
                      <p className="text-xs text-[#1A2E6E]/40 truncate">{lead.email}</p>
                    </div>
                    <span className="text-xs text-[#F5A623]/60 shrink-0">{timeAgo(lead.updated_at)}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {stats.cold_leads?.length > 0 && (
            <div className="bg-white rounded-xl border border-[#6366F1]/25 overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center gap-2">
                <Thermometer size={15} className="text-[#6366F1]" />
                <h3 className="text-sm font-semibold text-[#1A2E6E]">Cold Leads</h3>
                <span className="ml-auto text-xs bg-[#6366F1]/10 text-[#6366F1] px-2 py-0.5 rounded-full font-semibold">
                  {stats.cold_leads.length}
                </span>
              </div>
              <p className="px-5 pt-3 pb-1 text-xs text-[#1A2E6E]/50">Level tested, no payment after 48h — email sent</p>
              <div className="p-2 space-y-1">
                {stats.cold_leads.slice(0, 5).map(lead => (
                  <Link key={lead.id} to="/admin/students"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#6366F1]/5 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-[#6366F1]/10 flex items-center justify-center text-[#6366F1] font-bold text-sm shrink-0">
                      {lead.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A2E6E] truncate">{lead.name}</p>
                      <p className="text-xs text-[#1A2E6E]/40 truncate">{lead.email}</p>
                    </div>
                    <span className="text-xs text-[#6366F1]/50 shrink-0">{timeAgo(lead.updated_at)}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {stats.expiring_enrollments?.length > 0 && (
            <div className="bg-white rounded-xl border border-[#D72B2B]/25 overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center gap-2">
                <Timer size={15} className="text-[#D72B2B]" />
                <h3 className="text-sm font-semibold text-[#1A2E6E]">Expiring Soon</h3>
                <span className="ml-auto text-xs bg-[#D72B2B]/10 text-[#D72B2B] px-2 py-0.5 rounded-full font-semibold">
                  {stats.expiring_enrollments.length}
                </span>
                <Link to="/admin/billing" className="text-xs text-[#D72B2B] font-medium ml-2 flex items-center gap-0.5">
                  Billing <ChevronRight size={11} />
                </Link>
              </div>
              <p className="px-5 pt-3 pb-1 text-xs text-[#1A2E6E]/50">Active plans expiring within 7 days</p>
              <div className="p-2 space-y-1">
                {stats.expiring_enrollments.slice(0, 5).map((e, i) => (
                  <Link key={i} to="/admin/billing"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#D72B2B]/5 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-[#D72B2B]/10 flex items-center justify-center text-[#D72B2B] font-bold text-sm shrink-0">
                      {e.student_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A2E6E] truncate">{e.student_name}</p>
                      <p className="text-xs text-[#1A2E6E]/40">
                        {e.credits_remaining} credit{e.credits_remaining !== 1 ? 's' : ''} left
                      </p>
                    </div>
                    <span className="text-xs text-[#D72B2B]/60 shrink-0 font-medium">
                      {e.expires_at ? new Date(e.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pipeline */}
      {pipeline && (
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-semibold text-[#1A2E6E]">Student Pipeline</h2>
              <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Distribution across funnel stages</p>
            </div>
            <Link to="/admin/students" className="text-sm text-[#D72B2B] font-medium">Manage →</Link>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {FUNNEL_STAGES.map(({ key, label, icon: Icon, color, bg }) => {
              const count = pipeline[key] ?? 0;
              return (
                <motion.div key={key} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  className={`${bg} rounded-xl p-3 text-center`}>
                  <Icon className={`w-4 h-4 ${color} mx-auto mb-1`} />
                  <p className={`text-xl font-bold ${color}`}>{count}</p>
                  <p className="text-[10px] text-[#1A2E6E]/60 mt-0.5 leading-tight">{label}</p>
                </motion.div>
              );
            })}
          </div>
          {pipeline.total > 0 && (
            <div className="mt-4">
              <div className="flex rounded-full overflow-hidden h-1.5 gap-0.5">
                {FUNNEL_STAGES.map(({ key, bg }) => {
                  const pct = Math.round(((pipeline[key] ?? 0) / pipeline.total) * 100);
                  return pct > 0 ? <div key={key} className={`${bg}`} style={{ width: `${pct}%` }} /> : null;
                })}
              </div>
              <p className="text-xs text-[#1A2E6E]/40 mt-1.5 text-right">{pipeline.total} total</p>
            </div>
          )}
        </div>
      )}

      {/* Revenue Chart + Recent Registrations — chart is company-wide financial data, strict-admin only */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {isStrictAdmin && revenue.length > 0 && (
          <div className="lg:col-span-2 bg-white rounded-xl border border-[#E8EAF0] p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-semibold text-[#1A2E6E]">Revenue — Last 6 Months</h2>
                <p className="text-xs text-[#1A2E6E]/50 mt-0.5">Completed payments ({currency})</p>
              </div>
              <Link to="/admin/billing" className="text-sm text-[#D72B2B] font-medium">Billing →</Link>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={revenue} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8EAF0" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#1A2E6E99' }}
                  tickFormatter={m => new Date(Number(m.split('-')[0]), Number(m.split('-')[1]) - 1).toLocaleString('en', { month: 'short' })} />
                <YAxis
                  tick={{ fontSize: 10, fill: '#1A2E6E99' }}
                  tickFormatter={v => formatCurrencyBRL(v, { maximumFractionDigits: 0 })}
                  width={70}
                />
                <Tooltip
                  formatter={v => [formatCurrencyBRL(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'Revenue']}
                  labelFormatter={m => new Date(Number(m.split('-')[0]), Number(m.split('-')[1]) - 1).toLocaleString('en', { month: 'long', year: 'numeric' })}
                  contentStyle={{ border: '1px solid #E8EAF0', borderRadius: 10, fontSize: 12 }}
                />
                <Bar dataKey="total_brl" fill="#1A2E6E" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Recent Registrations */}
        <div className={`${isStrictAdmin && revenue.length > 0 ? '' : 'lg:col-span-3'} bg-white rounded-xl border border-[#E8EAF0] overflow-hidden`}>
          <div className="px-5 py-4 border-b border-[#E8EAF0] flex items-center justify-between">
            <h2 className="font-semibold text-[#1A2E6E] text-sm">New Students</h2>
            <Link to="/admin/students" className="text-xs text-[#D72B2B] font-medium flex items-center gap-1">
              All <ChevronRight size={12} />
            </Link>
          </div>
          <div className="divide-y divide-[#E8EAF0]">
            {stats?.recent_registrations?.length > 0 ? (
              stats.recent_registrations.map((s, i) => (
                <div key={s.id || i} className="flex items-center gap-3 px-5 py-3">
                  <div className="w-8 h-8 rounded-full bg-[#1A2E6E]/10 flex items-center justify-center text-[#1A2E6E] font-bold text-sm shrink-0">
                    {s.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A2E6E] truncate">{s.name}</p>
                    <p className="text-xs text-[#1A2E6E]/40 truncate">{s.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    s.status === 'approved' ? 'bg-[#4CAF50]/10 text-[#4CAF50]' : 'bg-[#F5A623]/10 text-[#F5A623]'
                  }`}>
                    {s.status}
                  </span>
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center">
                <p className="text-sm text-[#1A2E6E]/50">No recent registrations</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
